import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, ChevronDown, LogOut, Menu, Plus, User, X, FileText, Search, MessageSquare, Info, LifeBuoy, Shield } from "lucide-react";
import { logoutUser } from "../auth";
import RequestPartModal from "./RequestPartModal";
import "./UserHeaderResponsive.css";

const initials = (name) => (name?.trim()?.[0] || "M").toUpperCase();

export default function UserHeader({ unreadNotifications = 0 }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const profileRef = useRef(null);
  const drawerRef = useRef(null);
  const menuButtonRef = useRef(null);
  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch { return null; }
  })();

  const active = (path) => pathname === path || pathname.startsWith(`${path}/`);
  const go = (path) => { setDrawerOpen(false); setProfileOpen(false); navigate(path); };
  const requestPart = () => { setDrawerOpen(false); setRequestOpen(true); };
  const logout = async () => {
    setDrawerOpen(false);
    setProfileOpen(false);
    await logoutUser();
    navigate("/", { replace: true });
  };

  useEffect(() => {
    const onOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  useEffect(() => { setDrawerOpen(false); setProfileOpen(false); }, [pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (e.key === "Escape") setDrawerOpen(false);
      if (e.key === "Tab" && drawerRef.current) {
        const focusables = Array.from(drawerRef.current.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'));
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    drawerRef.current?.querySelector("button")?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      menuButtonRef.current?.focus();
    };
  }, [drawerOpen]);

  return (
    <>
      <header className="agp-user-header">
        <div className="agp-user-header__inner">
          <button ref={menuButtonRef} type="button" className="agp-mobile-menu-toggle" onClick={() => setDrawerOpen(true)} aria-label="Open navigation menu" aria-expanded={drawerOpen} aria-controls="agp-mobile-drawer">
            <Menu size={26} />
          </button>
          <img src="/logo white.png" alt="FindAutoParts" className="agp-header-logo" />
          <div className="dflex agp-header-desktop">
            <nav className="agp-user-nav" aria-label="Customer navigation">
              <button className={active("/quotes") ? "active" : ""} onClick={() => go("/quotes")}><FileText size={18} /> My Quotes</button>
              <button className={active("/search") ? "active" : ""} onClick={() => go("/search")}><Search size={18} /> Search</button>
              <button className={active("/comments") ? "active" : ""} onClick={() => go("/comments")}><MessageSquare size={18} /> Comments</button>
            </nav>
            <div className="agp-user-actions">
              <button className="agp-request-btn" onClick={requestPart}>
                <span className="agp-request-btn__icon"><Plus size={13} strokeWidth={3} color="black" /></span>
                Request Part
              </button>
              <button className="agp-bell" onClick={() => go("/notifications")} aria-label="Notifications">
                <Bell size={18} />
                {unreadNotifications > 0 && <span>{Math.min(unreadNotifications, 9)}</span>}
              </button>
              <div className="agp-profile" ref={profileRef}>
                <button className="agp-profile__button" onClick={() => setProfileOpen(v => !v)} aria-expanded={profileOpen}>
                  <span className="agp-profile__avatar">{initials(user?.name)}</span>
                  <span className="agp-profile__name">{user?.name || "User"}</span>
                  <ChevronDown size={14} className={profileOpen ? "rotate-180" : ""} />
                </button>
                {profileOpen && (
                  <div className="agp-profile__menu">
                    <button onClick={() => go("/profile")}><User size={16} /> Profile</button>
                    <button onClick={() => go("/about")}>About Us</button>
                    <button onClick={() => go("/support")}>Support</button>
                    <button onClick={() => go("/privacy-policy")}>Privacy Policy</button>
                    <button className="danger" onClick={logout}><LogOut size={16} /> Logout</button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <button className="agp-bell agp-mobile-bell" onClick={() => go("/notifications")} aria-label="Notifications">
            <Bell size={20} />
            {unreadNotifications > 0 && <span>{Math.min(unreadNotifications, 9)}</span>}
          </button>
        </div>
      </header>

      {drawerOpen && <div className="agp-mobile-overlay" onClick={() => setDrawerOpen(false)} aria-hidden="true" />}
      <aside id="agp-mobile-drawer" ref={drawerRef} className={`agp-mobile-drawer${drawerOpen ? " is-open" : ""}`} role="dialog" aria-modal={drawerOpen ? "true" : undefined} aria-label="Mobile navigation" aria-hidden={!drawerOpen} inert={!drawerOpen}>
        <div className="agp-mobile-drawer__top">
          <img src="/logo white.png" alt="FindAutoParts" />
          <button onClick={() => setDrawerOpen(false)} aria-label="Close navigation"><X size={24} /></button>
        </div>
        <div className="agp-mobile-drawer__user">
          <span className="agp-profile__avatar">{initials(user?.name)}</span>
          <div><strong>{user?.name || "User"}</strong><small>My Account</small></div>
        </div>
        <nav className="agp-mobile-drawer__nav" aria-label="Mobile customer navigation">
          <button className={active("/quotes") ? "active" : ""} onClick={() => go("/quotes")}><FileText size={18} /> My Quotes</button>
          <button className={active("/search") ? "active" : ""} onClick={() => go("/search")}><Search size={18} /> Search</button>
          <button className={active("/comments") ? "active" : ""} onClick={() => go("/comments")}><MessageSquare size={18} /> Comments</button>
          <button className={active("/notifications") ? "active" : ""} onClick={() => go("/notifications")}><Bell size={18} /> Notifications {unreadNotifications > 0 && <span className="agp-drawer-count">{unreadNotifications}</span>}</button>
          <button className={active("/profile") ? "active" : ""} onClick={() => go("/profile")}><User size={18} /> Profile</button>
          <button className={active("/about") ? "active" : ""} onClick={() => go("/about")}><Info size={18} /> About Us</button>
          <button className={active("/support") ? "active" : ""} onClick={() => go("/support")}><LifeBuoy size={18} /> Support</button>
          <button className={active("/privacy-policy") ? "active" : ""} onClick={() => go("/privacy-policy")}><Shield size={18} /> Privacy Policy</button>
        </nav>
        <div className="agp-mobile-drawer__bottom">
          <button className="agp-mobile-drawer__request" onClick={requestPart}><Plus size={18} /> Request Part</button>
          <button className="agp-mobile-drawer__logout" onClick={logout}><LogOut size={18} /> Logout</button>
        </div>
      </aside>
      <RequestPartModal open={requestOpen} onClose={() => setRequestOpen(false)} onSubmitted={() => navigate("/quotes")} />
    </>
  );
}
